import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCityView,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGolfCourseView,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMountainView,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasParkView,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTerritorialView,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWaterView,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWoodedView,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { type AmenityFlagSave, getIsInputFormDisabled } from '../../utils';

import { VIEW_KEYS, VIEW_METADATA, type ViewKey } from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<ViewKey, boolean>;

const ViewsAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const viewTypes = listingDetails?.buildingAttributes?.viewTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasCity: viewTypes?.hasCity ?? false,
    hasGolfCourse: viewTypes?.hasGolfCourse ?? false,
    hasMountain: viewTypes?.hasMountain ?? false,
    hasPark: viewTypes?.hasPark ?? false,
    hasTerritorial: viewTypes?.hasTerritorial ?? false,
    hasWater: viewTypes?.hasWater ?? false,
    hasWooded: viewTypes?.hasWooded ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Views updated',
  });

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updateCity } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCityView,
    skipToast: true,
    meta: { context: { api: 'hasCityView', details: { listingAlias } } },
  });

  const { mutateAsync: updateGolfCourse } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGolfCourseView,
    skipToast: true,
    meta: { context: { api: 'hasGolfCourseView', details: { listingAlias } } },
  });

  const { mutateAsync: updateMountain } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMountainView,
    skipToast: true,
    meta: { context: { api: 'hasMountainView', details: { listingAlias } } },
  });

  const { mutateAsync: updatePark } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasParkView,
    skipToast: true,
    meta: { context: { api: 'hasParkView', details: { listingAlias } } },
  });

  const { mutateAsync: updateTerritorial } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTerritorialView,
    skipToast: true,
    meta: { context: { api: 'hasTerritorialView', details: { listingAlias } } },
  });

  const { mutateAsync: updateWater } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWaterView,
    skipToast: true,
    meta: { context: { api: 'hasWaterView', details: { listingAlias } } },
  });

  const { mutateAsync: updateWooded } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWoodedView,
    skipToast: true,
    meta: { context: { api: 'hasWoodedView', details: { listingAlias } } },
  });

  const mutationMap = {
    hasCity: updateCity,
    hasGolfCourse: updateGolfCourse,
    hasMountain: updateMountain,
    hasPark: updatePark,
    hasTerritorial: updateTerritorial,
    hasWater: updateWater,
    hasWooded: updateWooded,
  } as unknown as Record<keyof LocalState, AmenityFlagSave>;

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as ViewKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = VIEW_METADATA[fieldName];
      await mutationMap[fieldName]({
        postData: {
          [metadata.apiKey]: checked,
          listingAlias,
        },
      });
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Views</Legend>} disabled={isInputFormDisabled}>
      {VIEW_KEYS.map((fieldName) => {
        const metadata = VIEW_METADATA[fieldName];
        return (
          <LabeledControl
            key={`viewTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(ViewsAmenities);
